import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { contentContext } from "../../context/contentContext";

// Single-file, data-driven SideMenu component
export default function SideMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = (localStorage.getItem("userType") || "").toLowerCase();

  const {
    setMenuLearning,
    setMenuState,
    setMenuSettings,
    setUserSearch,
    toggleMenu,
    setToggleMenu,
  } = useContext(contentContext);

  // ui state
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [openSection, setOpenSection] = useState(userType || null);
  const [userToggled, setUserToggled] = useState(false);

  // helper: mark a path active

  // simple text-capitalize
  const capitalizeWords = (s) =>
    s
      ? s
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
      : "";

  // unified menu definitions (id must be unique)
  const menus = useMemo(() => ({
    admin: [
      { path: "/biopsy/cases", label: "Biopsy", icon: "grid_view", id: "admin-0" },
      {
        label: "Learning Hub",
        icon: "school",
        id: "admin-1",
        subMenus: [
          { path: "/cases", label: "Cases", menuKey: "cases", id: "admin-1-0" },
          { path: "/sharedlinks", label: "Shared Links", menuKey: "sharedlinks", id: "admin-1-6" },
          { path: "/lecturelibrary", label: "Video Library", menuKey: "videolibraryadmin", id: "admin-1-1" },
          // { path: "/feedbacks", label: "Feedback", menuKey: "feedbacksadmin", id: "admin-1-4" },
          { path: "/modules", label: "Modules", menuKey: "modules", id: "admin-1-2" },
          { path: "/cpcmanagement", label: "CPC Management", menuKey: "cpcmanagement", id: "admin-1-3" },
          { path: "/biopsy/cases?type=learning", label: "Learning Cases", menuKey: "learningcases", id: "admin-1-5" },
        ],
      },
      { path: "/fellowshiplist", label: "Fellowship", icon: "assignment", id: "admin-2" },
      { path: "/feedbacks", label: "Feedback", icon: "feedback", id: "admin-6" },
      { path: "/transctions", label: "Transactions", icon: "receipt_long", id: "admin-3" },
      { path: "/registeredusers", label: "App Users", icon: "group", id: "admin-4" },
      { path: "/users", label: "Admin Users", icon: "account_box", id: "admin-5" },
      { path: "/sendEmail", label: "Email Manager", icon: "mail", id: "admin-9" },
      {
        label: "Lookups",
        icon: "search",
        id: "admin-8",
        subMenus: [
          { path: "/tests", label: "Tests", menuKey: "tests", id: "admin-8-0" },
          { path: "/biopsy/types", label: "Biopsy types", menuKey: "biopsytypes", id: "admin-8-1" },
          { path: "/answers", label: "Answers", menuKey: "answers", id: "admin-8-2" },
        ],
      },
      {
        label: "Settings",
        icon: "settings",
        id: "admin-7",
        subMenus: [{ path: "/subscriptionplans", label: "Subscriptions", menuKey: "subscriptions", id: "admin-7-0" }],
      },
    ],

    doctor: [
      { path: "/cpc", label: "CPC", icon: "video_library", id: "doctor-0" },
      { path: "/timedmodule", label: "Timed Module", icon: "video_library", id: "doctor-1" },
      { path: "/lecture-library", label: "Video Library", icon: "category", id: "doctor-2" },
      {
        label: "Learning Module",
        icon: "category",
        id: "doctor-3",
        subMenus: [
          { path: "/moduleslibrary", label: "All Modules", menuKey: "allModules", id: "doctor-3-0" },
          { path: "/usermoduleslibrary", label: "My Modules", menuKey: "myModules", id: "doctor-3-1" },
          { path: "/mcqmoduleslibrary", label: "MCQ Modules", menuKey: "mcqModules", id: "doctor-3-2" },
          { path: "/fellowshipmodules", label: "Fellowship Modules", menuKey: "fellowshipModules", id: "doctor-3-3" },
        ],
      },
      { path: "/biopsy/cases/doctor", label: "Biopsy", icon: "grid_view", id: "doctor-4" },
      { path: "/transactiondata", label: "Transactions", icon: "receipt_long", id: "doctor-5" },
    ],

    // shown only inside the admin sidebar's collapsible "Doctor" section —
    // routes to the admin-only doctor-filtered biopsy list, not the doctor's own view
    adminDoctorView: [
      { path: "/cpc", label: "CPC", icon: "video_library", id: "admin-doctor-0" },
      { path: "/timedmodule", label: "Timed Module", icon: "video_library", id: "admin-doctor-1" },
      { path: "/lecture-library", label: "Video Library", icon: "category", id: "admin-doctor-2" },
      {
        label: "Learning Module",
        icon: "category",
        id: "admin-doctor-3",
        subMenus: [
          { path: "/moduleslibrary", label: "All Modules", menuKey: "allModules", id: "admin-doctor-3-0" },
          { path: "/usermoduleslibrary", label: "My Modules", menuKey: "myModules", id: "admin-doctor-3-1" },
          { path: "/mcqmoduleslibrary", label: "MCQ Modules", menuKey: "mcqModules", id: "admin-doctor-3-2" },
          { path: "/fellowshipmodules", label: "Fellowship Modules", menuKey: "fellowshipModules", id: "admin-doctor-3-3" },
        ],
      },
      { path: "/biopsy/admin/doctor", label: "Biopsy", icon: "grid_view", id: "admin-doctor-4" },
      { path: "/transactiondata", label: "Transactions", icon: "receipt_long", id: "admin-doctor-5" },
    ],

    pathologist: [
      { path: "/biopsy/cases/pathologist", label: "Biopsy", icon: "grid_view", id: "pathologist-0" },
      { path: "/fellows", label: "Fellows", icon: "group", id: "pathologist-1" },
    ],

    // shown only inside the admin sidebar's collapsible "Pathologist" section —
    // routes to the admin-only list of pathologist-assigned cases, not the pathologist's own view
    adminPathologistView: [
      { path: "/biopsy/admin/pathologist", label: "Biopsy", icon: "grid_view", id: "admin-pathologist-0" },
      { path: "/fellows", label: "Fellows", icon: "group", id: "admin-pathologist-1" },
    ],

    "lab technician": [
      { path: "/biopsy/cases", label: "Biopsy", icon: "grid_view", id: "labtech-0" },
      // { path: "", label: "Assigned Cases", icon: "account_box", id: "labtech-1" },
      // { label: "Additional Test Requests", icon: "quick_reference_all", id: "labtech-2" },
      // { label: "Payment Reports", icon: "library_books", id: "labtech-3" },
    ],
     "teaching admin": [
      { path: "/biopsy/cases", label: "Biopsy", icon: "grid_view", id: "teachingadmin-0" },
    ],

  }), []);

console.log("menu",menus)
  // REPLACE existing isActive with this:
  const isActive = useCallback((path, menuKey) => {
    if (!path) return "";
    const currentFull = location.pathname || "";
    // ignore query/hash
    const current = currentFull.split(/[?#]/)[0];

    // exact match or sub-path match (e.g. /moduleslibrary and /moduleslibrary/123)
    const matchesPath = (p) => {
      if (!p) return false;
      const normalized = p.split(/[?#]/)[0];
      return current === normalized || current.startsWith(normalized + "/");
    };

    const fromMenu = localStorage.getItem("fromMenu");
    if (matchesPath(path)) return "active";
    if (current.startsWith("/result/") && fromMenu === menuKey) return "active";
    return "";
  }, [location.pathname]);

  // REPLACE the useEffect that auto-opens dropdowns with this:
  useEffect(() => {
    if (userToggled) return;

    const currentFull = location.pathname || "";
    const current = currentFull.split(/[?#]/)[0];

    const findActiveDropdown = () => {
      const all = Object.values(menus).flat();
      for (const menu of all) {
        if (!menu.subMenus) continue;
        for (const sub of menu.subMenus) {
          if (!sub.path) continue;
          const normalized = sub.path.split(/[?#]/)[0];
          if (current === normalized || current.startsWith(normalized + "/")) {
            return menu.id;
          }
        }
      }
      return null;
    };

    setActiveDropdown(findActiveDropdown());
  }, [location.pathname, userToggled, menus]);


  // navigate helper
  const handleMenuClick = (path, menuKey, parentId = null) => {
    setUserSearch("");
    localStorage.setItem("fromMenu", menuKey || "");
    setMenuLearning(false);
    setMenuState(false);
    setMenuSettings(false);
    setToggleMenu(false);
    setActiveDropdown(parentId);
    setUserToggled(true);
    if (path) navigate(path);
  };

  const handleDropdownToggle = (id) => {
    setActiveDropdown((prev) => (prev === id ? null : id));
    setUserToggled(true);
  };

  // utility to dedupe by path while preserving order
  const uniqueMenulist = (list) => {
    const seen = new Set();
    return list
      .map((m) => {
        if (!m.subMenus) {
          if (m.path && seen.has(m.path)) return null;
          if (m.path) seen.add(m.path);
          return m;
        }
        const filtered = (m.subMenus || []).filter((s) => {
          if (!s.path) return false; // drop items without a path
          if (seen.has(s.path)) return false;
          seen.add(s.path);
          return true;
        });
        if (filtered.length === 0) return null;
        return { ...m, subMenus: filtered };
      })
      .filter(Boolean);
  };

  const renderMenuList = (list, title) => {
    const unique = uniqueMenulist(list || []);
    const sectionOpen = openSection === title;

    return (
      <div key={title}>
        <div
          className="metismenu list-unstyled main-menu-title-container"
          style={{ cursor: "pointer" }}
          onClick={() => setOpenSection((p) => (p === title ? null : title))}
        >
          <h6 className="text-light ms-2 main-menu-title card-title waves-effect">{capitalizeWords(title)}</h6>
        </div>

        <div style={{ overflow: "hidden", transition: "max-height 0.25s ease", maxHeight: sectionOpen ? "2000px" : "0px" }}>
          <ul className="metismenu list-unstyled" id="side-menu">
            {unique.map((m) => (
              <li key={m.id} onClick={() => !m.subMenus && handleMenuClick(m.path, m.menuKey)}>
                {!m.subMenus ? (
                  <a className={`waves-effect ${m.path ? isActive(m.path, m.menuKey) : ""}`}>
                    {m.icon && <i className="material-symbols-outlined">{m.icon}</i>}
                    <span>{m.label}</span>
                  </a>
                ) : (
                  <>
                    <a className="has-arrow waves-effect" onClick={() => handleDropdownToggle(m.id)}>
                      {m.icon && <i className="material-symbols-outlined">{m.icon}</i>}
                      <span>{m.label}</span>
                    </a>
                    <ul className={`sub-menu mm-collapse ${activeDropdown === m.id ? "mm-show" : ""}`} aria-expanded={activeDropdown === m.id}>
                      {m.subMenus.map((s) => (
                        <li key={s.id} onClick={() => handleMenuClick(s.path, s.menuKey, m.id)} className={`waves-effect ${isActive(s.path, s.menuKey)}`}>
                          <a>{s.label}</a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className={toggleMenu ? `quiz-page-side vertical-menu menu-show` : `quiz-page-side vertical-menu`}>
      <div data-simplebar className="h-100">
        <div id="sidebar-menu">
          {userType === "admin" ? (
            <>
              {renderMenuList(menus.admin, "admin")}
              {renderMenuList(menus.adminDoctorView, "doctor")}
              {renderMenuList(menus.adminPathologistView, "pathologist")}
              {renderMenuList(menus["lab technician"], "lab technician")}
            </>
          ) : (
            <>
              {userType === "doctor" && renderMenuList(menus.doctor, "doctor")}
              {userType === "pathologist" && renderMenuList(menus.pathologist, "pathologist")}
              {userType === "lab technician" && renderMenuList(menus["lab technician"], "lab technician")}
                {userType === "teaching admin" && renderMenuList(menus["teaching admin"], "teaching admin")}
            </>
          )}
        </div>
        <p className="activity_time">Action: {localStorage.getItem("timeActivity")}</p>
      </div>
    </div>
  );
}
